/* The attention list, fetched, filtered by what has been dismissed, and dismissable.
 *
 * Reads through the same query keys the sidebar counts and the dashboard use, so the bell costs no
 * extra requests for those: TanStack serves all of them from one fetch.
 */
import { useCallback, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { buildExpiries, dueWithin } from '../lib/expiries';
import {
  buildAttention,
  DISMISSED_KEY,
  mergeDismissed,
  parseDismissed,
  visibleAttention,
  type AttentionItem,
} from '../lib/attention';
import { visibleLicences } from '../lib/registers';
import { qk } from '../query/keys';
import { useServices } from '../services/context';

export interface Attention {
  /** Not yet dismissed: what the bell counts and lists. */
  items: AttentionItem[];
  /** Everything currently true, dismissed or not. */
  all: AttentionItem[];
  loading: boolean;
  dismiss: (ids: string[]) => void;
}

export function useAttention(): Attention {
  const svc = useServices();
  const qc = useQueryClient();

  const devices = useQuery({ queryKey: qk.devices, queryFn: () => svc.devices.list() });
  const people = useQuery({ queryKey: qk.people, queryFn: () => svc.people.list() });
  const suppliers = useQuery({ queryKey: qk.suppliers, queryFn: () => svc.suppliers.list() });
  const domains = useQuery({ queryKey: qk.domains, queryFn: () => svc.domains.list() });
  const applications = useQuery({ queryKey: qk.applications, queryFn: () => svc.applications.list() });
  const credentials = useQuery({ queryKey: qk.credentials, queryFn: () => svc.credentials.list() });
  const articles = useQuery({ queryKey: qk.articles, queryFn: () => svc.articles.list() });
  const licences = useQuery({ queryKey: qk.licences, queryFn: () => svc.licences.skus() });
  const recordTypes = useQuery({ queryKey: qk.recordTypes, queryFn: () => svc.recordTypes.types() });
  const customRecords = useQuery({ queryKey: qk.customRecords(), queryFn: () => svc.recordTypes.records() });
  const tiles = useQuery({ queryKey: qk.statusTiles, queryFn: () => svc.status.tiles() });
  const lastSync = useQuery({ queryKey: qk.sync, queryFn: () => svc.sync.lastRun() });
  /* Its own key, under the audit prefix: the dashboard asks for eight entries and the audit screen
     for five hundred under the plain key, and a list of renames needs a hundred. Anything that
     invalidates the audit log still invalidates this. */
  const audit = useQuery({ queryKey: [...qk.audit, 'attention'], queryFn: () => svc.audit.list(100) });
  const settings = useQuery({ queryKey: qk.settings, queryFn: () => svc.settings.all() });

  const all = useMemo(() => {
    const expiries = dueWithin(
      buildExpiries({
        devices: devices.data,
        people: people.data,
        suppliers: suppliers.data,
        domains: domains.data,
        applications: applications.data,
        credentials: credentials.data,
        articles: articles.data,
        skus: visibleLicences(licences.data ?? []),
        recordTypes: recordTypes.data,
        customRecords: customRecords.data,
      }),
      90,
    );
    return buildAttention({
      devices: devices.data,
      people: people.data,
      expiries,
      tiles: tiles.data,
      audit: audit.data,
      lastSync: lastSync.data,
    });
  }, [
    devices.data, people.data, suppliers.data, domains.data, applications.data, credentials.data,
    articles.data, licences.data, recordTypes.data, customRecords.data, tiles.data, audit.data,
    lastSync.data,
  ]);

  const dismissed = useMemo(() => parseDismissed(settings.data?.[DISMISSED_KEY]), [settings.data]);
  const items = useMemo(() => visibleAttention(all, dismissed), [all, dismissed]);

  const write = useMutation({
    mutationFn: async (ids: string[]) => {
      /* Merged into what is stored NOW, not what this page last read, so a dismissal somebody
         else made a minute ago is kept. */
      const fresh = await svc.settings.all();
      const next = mergeDismissed(parseDismissed(fresh[DISMISSED_KEY]), ids, new Date());
      await svc.settings.set(DISMISSED_KEY, JSON.stringify(next));
      return JSON.stringify(next);
    },
    onMutate: async (ids: string[]) => {
      /* Gone from the bell the instant it is pressed, and put back if the write fails. */
      await qc.cancelQueries({ queryKey: qk.settings });
      const before = qc.getQueryData<Record<string, string>>(qk.settings);
      const merged = mergeDismissed(parseDismissed(before?.[DISMISSED_KEY]), ids, new Date());
      qc.setQueryData(qk.settings, { ...(before ?? {}), [DISMISSED_KEY]: JSON.stringify(merged) });
      return { before };
    },
    onError: (_e, _ids, ctx) => {
      if (ctx?.before) qc.setQueryData(qk.settings, ctx.before);
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: qk.settings }),
  });

  const dismiss = useCallback((ids: string[]) => {
    if (ids.length > 0) write.mutate(ids);
  }, [write]);

  /* Every source, not just the first few. The bell takes its "already there" baseline from the first
     settled list (isPending, not isLoading: a query that has not started yet is not loading, and
     is not done either); taking it while the expiry sources or the audit log were still arriving made
     everything that turned up a moment later look new, and toasted it. */
  const loading = [
    devices, people, suppliers, domains, applications, credentials, articles, licences,
    recordTypes, customRecords, tiles, lastSync, audit, settings,
  ].some((q) => q.isPending);
  return { items, all, loading, dismiss };
}
